<template lang="pug">
div
  b-row(v-show="isLoading")
    b-col
      b-card(class="py-5")
        loading(:active="true", :is-full-page="false", color="#498ceb")

  div(v-show="!isLoading")
    //- Header
    b-card
      b-row
        b-col(md="8")
          h4 {{ request.caseNumber }}
          b-badge.mr-1(:variant="getStageVariant(request.stage)")
            | {{ $t(`arsopbl.stage_${request.stage}`) }}
          b-badge.mr-1(:variant="deadlineVariant")
            | {{ deadlineDays }} {{ $t('arsopbl.business_days_short') }}
          b-badge(:variant="statusVariant")
            | {{ $t(`arsopbl.status_${requestStatus}`) }}
        b-col(md="4", class="text-right")
          b-button(
            variant="secondary",
            :to="{ name: 'privacy-arsopbl-list' }"
          )
            feather-icon(icon="ArrowLeftIcon", class="mr-50")
            | {{ $t('Back') }}

    //- Stage content - Recepción
    b-card(v-if="stageNumber >= 1")
      h5 {{ $t('arsopbl.stage_reception') }}
      b-row
        b-col(md="4")
          strong {{ $t('arsopbl.right_exercised') }}:
          p {{ rightLabel }}
        b-col(md="4")
          strong {{ $t('arsopbl.titular') }}:
          p {{ (request.titular && request.titular.fullName) || '-' }}
        b-col(md="4")
          strong {{ $t('arsopbl.rut_passport') }}:
          p {{ (request.titular && request.titular.identification) || '-' }}
      b-row
        b-col(md="4")
          strong {{ $t('arsopbl.email') }}:
          p {{ (request.titular && request.titular.email) || '-' }}
        b-col(md="4")
          strong {{ $t('arsopbl.phone') }}:
          p {{ (request.titular && request.titular.phone) || '-' }}
        b-col(md="4")
          strong {{ $t('arsopbl.identity_document') }}:
          div
            b-button(
              v-if="request.identityDocumentKey",
              size="sm",
              variant="outline-info",
              @click="handleDownload(request.identityDocumentKey)"
            )
              feather-icon(icon="DownloadIcon", class="mr-25")
              | {{ $t('arsopbl.download') }}
            span(v-else) -
      b-row
        b-col(md="12")
          strong {{ $t('arsopbl.description_label') }}:
          p {{ request.description || '-' }}
        b-col(md="12", v-if="request.additionalInfo")
          strong {{ $t('arsopbl.additional_info') }}:
          p {{ request.additionalInfo }}

      div(v-if="request.stage === 'reception' && canEdit")
        hr
        b-button(variant="primary", @click="handleAdvance('identity_validation')")
          | {{ $t('arsopbl.advance_to_identity_validation') }}

    //- Stage 2 - Validación de identidad
    b-card(v-if="stageNumber >= 2")
      h5 {{ $t('arsopbl.stage_identity_validation') }}
      b-form-checkbox(v-model="identityVerified", :disabled="request.stage !== 'identity_validation'")
        | {{ $t('arsopbl.identity_verified') }}
      b-form-group.mt-1(v-if="identityVerified", :label="$t('arsopbl.identity_note')")
        b-form-textarea(v-model="identityNote", rows="2", :disabled="request.stage !== 'identity_validation'")
      b-form-group.mt-1(v-else, :label="$t('arsopbl.rejection_reason')")
        b-form-textarea(v-model="rejectionReason", rows="2", :disabled="request.stage !== 'identity_validation'")

      div(v-if="request.stage === 'identity_validation' && canEdit")
        hr
        b-button.mr-1(variant="primary", @click="advanceToAnalysis", :disabled="!identityVerified")
          | {{ $t('arsopbl.advance_to_analysis') }}
        b-button(variant="danger", @click="handleRejectIdentity", :disabled="identityVerified || !rejectionReason.trim()")
          | {{ $t('arsopbl.reject_identity') }}

    //- Stage 3 - Análisis de procedencia
    b-card(v-if="stageNumber >= 3")
      h5 {{ $t('arsopbl.stage_analysis') }}
      b-form-group(:label="$t('arsopbl.is_right_applicable')")
        b-form-textarea(v-model="analysisApplicable", rows="2", :disabled="request.stage !== 'analysis'")
      b-form-group(:label="$t('arsopbl.exception_exists')")
        b-form-textarea(v-model="analysisException", rows="2", :disabled="request.stage !== 'analysis'")
      b-form-group(:label="$t('arsopbl.attachments')")
        document-repository(
          :documents="analysisDocs",
          :keys="request.analysisDocumentKeys || []",
          :read-only="request.stage !== 'analysis'",
          @add="handleAddAnalysisDocs",
          @delete="handleDeleteAnalysisDoc"
        )

      div(v-if="request.stage === 'analysis' && canEdit")
        hr
        b-button(variant="primary", @click="advanceToExecution", :disabled="isUploading")
          | {{ $t('arsopbl.advance_to_execution') }}

    //- Stage 4 - Ejecución
    b-card(v-if="stageNumber >= 4")
      h5 {{ $t('arsopbl.stage_execution') }}
      b-form-group(:label="$t('arsopbl.execution_responsible')")
        v-select(
          v-model="executionResponsible",
          :options="workersOptions",
          :reduce="opt => opt.value",
          label="text",
          :disabled="request.stage !== 'execution'",
          :placeholder="$t('arsopbl.select_responsible')"
        )
      b-form-group(:label="$t('arsopbl.actions_to_execute')")
        b-form-textarea(v-model="executionActions", rows="3", :disabled="request.stage !== 'execution'")
      b-form-group(:label="$t('arsopbl.attachments')")
        document-repository(
          :documents="executionDocs",
          :keys="request.executionDocumentKeys || []",
          :read-only="request.stage !== 'execution'",
          @add="handleAddExecutionDocs",
          @delete="handleDeleteExecutionDoc"
        )

      div(v-if="request.stage === 'execution' && canEdit")
        hr
        b-button(variant="primary", @click="advanceToResponse", :disabled="isUploading")
          | {{ $t('arsopbl.advance_to_response') }}

    //- Stage 5 - Respuesta
    b-card(v-if="stageNumber >= 5")
      h5 {{ $t('arsopbl.stage_response') }}
      b-form-group(v-if="request.stage === 'response'", :label="$t('arsopbl.response_template')")
        v-select(
          v-model="selectedTemplate",
          :options="templateOptions",
          :reduce="opt => opt.value",
          label="text",
          :placeholder="$t('arsopbl.select_template')",
          @input="applyTemplate"
        )
      b-form-group(:label="$t('arsopbl.formal_response')")
        quill-editor(v-if="request.stage === 'response'", v-model="formalResponse", :options="editorOption")
        .border.p-2.rounded(v-else, v-html="formalResponse")
      b-form-group(:label="$t('arsopbl.attachments')")
        document-repository(
          :documents="responseDocs",
          :keys="request.responseDocumentKeys || []",
          :read-only="request.stage !== 'response'",
          @add="handleAddResponseDocs",
          @delete="handleDeleteResponseDoc"
        )

      div(v-if="request.stage === 'response' && canEdit")
        hr
        b-button(variant="success", @click="handleSendResponse", :disabled="isUploading")
          feather-icon(icon="SendIcon", class="mr-50")
          | {{ $t('arsopbl.send_response_and_close') }}

    //- Stage 6 - Cierre
    b-card(v-if="stageNumber >= 6")
      h5 {{ $t('arsopbl.stage_closure') }}
      b-row
        b-col(md="4")
          strong {{ $t('arsopbl.closure_status') }}:
          p {{ $t(`arsopbl.status_${request.status}`) }}
        b-col(md="4")
          strong {{ $t('arsopbl.closure_date') }}:
          p {{ formatDate(request.closedAt) }}

    //- Audit log (sistema unificado)
    audit-log-table(
      v-if="request && request.history"
      :history="request.history || []"
      :field-translations="{\
        stage: $t('arsopbl.stage_label'),\
        identityVerified: $t('arsopbl.identity_verified'),\
        identityNote: $t('arsopbl.identity_note'),\
        rejectionReason: $t('arsopbl.rejection_reason'),\
        analysisApplicable: $t('arsopbl.is_right_applicable'),\
        analysisException: $t('arsopbl.exception_exists'),\
        analysisDocumentKeys: $t('arsopbl.attachments'),\
        executionResponsible: $t('arsopbl.execution_responsible'),\
        executionActions: $t('arsopbl.actions_to_execute'),\
        executionDocumentKeys: $t('arsopbl.attachments'),\
        formalResponse: $t('arsopbl.formal_response'),\
        responseDocumentKeys: $t('arsopbl.attachments'),\
        status: $t('arsopbl.status_label')\
      }"
    )
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { BBadge, BFormCheckbox } from 'bootstrap-vue'
import vSelect from 'vue-select'
import { useRouter } from '@core/utils/utils'
import store from '@/store'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useCommonArsopbl, { STAGES } from '../useCommonArsopbl'
import DocumentRepository from '@/views/habit/DocumentRepository'
import AuditLogTable from '@/components/AuditLogTable.vue'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

export default {
  components: { BBadge, BFormCheckbox, vSelect, DocumentRepository, AuditLogTable, quillEditor, Loading },
  setup() {
    const { route } = useRouter()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { getItemsAggregate, getItems } = useLambda()
    const {
      collection, advanceStage, closeRequest, downloadFile, getDeadlineStatus, uploadFileToS3, getTemplates,
      sendStageChangeNotification, sendResponsibleAssignedNotification, sendClosureNotification,
    } = useCommonArsopbl()
    const userData = store.state?.userStore?.userData
    const clientId = userData.client.$oid

    const requestId = route.value.params.id
    const request = ref({})
    const originalRequest = ref({}) // Copia sin transformar para detección de cambios
    const isLoading = ref(true)
    const isUploading = ref(false)
    const workersOptions = ref([])

    // Response templates
    const templates = ref([])
    const selectedTemplate = ref(null)
    const editorOption = {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, false] }],
          ['link'],
          ['clean'],
        ],
      },
      placeholder: i18n.t('arsopbl.formal_response_placeholder'),
    }

    // Stage attachments (local files pending upload)
    const analysisDocs = ref([])
    const executionDocs = ref([])
    const responseDocs = ref([])

    // Stage form data
    const identityVerified = ref(false)
    const identityNote = ref('')
    const rejectionReason = ref('')
    const analysisApplicable = ref('')
    const analysisException = ref('')
    const executionResponsible = ref(null)
    const executionActions = ref('')
    const formalResponse = ref('')

    const canEdit = computed(() => hasPermission('privacy-arsopbl-list', 'edit'))

    const stageNumber = computed(() => {
      return STAGES[request.value.stage] || 1
    })

    const deadlineVariant = computed(() => {
      return request.value.deadlineStatus?.variant || 'light-info'
    })

    const deadlineDays = computed(() => {
      return request.value.deadlineStatus?.days || 0
    })

    const statusVariant = computed(() => {
      const variants = {
        open: 'light-info',
        resolved: 'light-success',
        rejected: 'light-danger',
      }
      return variants[request.value.status] || 'light-secondary'
    })

    const requestStatus = computed(() => {
      return request.value.status || 'open'
    })

    const rightLabel = computed(() => {
      const code = request.value.rightCode || ''
      return `${i18n.t('arsopbl.right_' + code.toLowerCase())} (${code})`
    })

    const templateOptions = computed(() => templates.value
      .filter(t => !t.rightCode || t.rightCode === request.value.rightCode)
      .map(t => ({ value: t._id.toString(), text: t.name, content: t.content })))

    const templateVariables = computed(() => {
      const r = request.value
      const titular = r.titular || {}
      return {
        caseNumber: r.caseNumber || '',
        'titular.nombre': titular.fullName || '',
        'titular.identificacion': titular.identification || '',
        'titular.email': titular.email || '',
        'titular.telefono': titular.phone || '',
        derecho: r.rightCode ? `${i18n.t(`arsopbl.right_${r.rightCode.toLowerCase()}`)} (${r.rightCode})` : '',
        fecha: formatDate(new Date()),
        fechaRecepcion: formatDate(r.createdAt),
      }
    })

    const renderTemplate = (content) => content.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key) => {
      const value = templateVariables.value[key]
      return value !== undefined ? value : match
    })

    const applyTemplate = (templateId) => {
      if (!templateId) return
      const tpl = templateOptions.value.find(t => t.value === templateId)
      if (tpl) formalResponse.value = renderTemplate(tpl.content)
    }

    const getStageVariant = (stage) => {
      const variants = {
        reception: 'light-info',
        identity_validation: 'light-warning',
        analysis: 'light-primary',
        execution: 'light-info',
        response: 'light-success',
        closure: 'light-secondary',
      }
      return variants[stage] || 'light-info'
    }

    const formatDate = (dateValue) => {
      if (!dateValue) return '-'
      const raw = dateValue?.$date || dateValue
      const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
      return new Date(raw).toLocaleDateString(`${default_language || 'es'}-CL`, { timeZone: 'UTC' })
    }

    const fetchRequest = async () => {
      isLoading.value = true
      try {
        const pipeline = [
          { $match: { _id: oid(requestId), client_id: oid(clientId) } },
          { $addFields: { _id: { $toString: '$_id' } } },
        ]
        const items = await getItemsAggregate({ collection, pipeline })
        if (items?.[0]) {
          const doc = items[0]
          doc.deadlineStatus = getDeadlineStatus(doc.createdAt?.$date || doc.createdAt)

          // Migrar auditLog → history para sistema unificado
          if (!doc.history) {
            doc.history = doc.auditLog || []
          }

          // Guardar copia SIN transformar para detección de cambios
          originalRequest.value = JSON.parse(JSON.stringify(doc))

          // Populate form fields from existing data
          if (doc.identityVerified) identityVerified.value = doc.identityVerified
          if (doc.identityNote) identityNote.value = doc.identityNote
          if (doc.rejectionReason) rejectionReason.value = doc.rejectionReason
          if (doc.analysisApplicable) analysisApplicable.value = doc.analysisApplicable
          if (doc.analysisException) analysisException.value = doc.analysisException
          if (doc.executionResponsible) executionResponsible.value = doc.executionResponsible?.$oid || doc.executionResponsible
          if (doc.executionActions) executionActions.value = doc.executionActions
          if (doc.formalResponse) formalResponse.value = doc.formalResponse

          request.value = doc
        }
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_fetch'))
      } finally {
        isLoading.value = false
      }
    }

    const fetchWorkers = async () => {
      try {
        const items = await getItems({
          collection: 'worker',
          query: { client_id: oid(userData.client.$oid), deleted: { $ne: true } },
          options: { sort: { name: 1 } },
        })
        workersOptions.value = (items || []).map(w => ({ value: w._id.toString(), text: w.name }))
      } catch (error) {
        console.log(error)
      }
    }

    const fetchTemplates = async () => {
      try {
        templates.value = await getTemplates() || []
      } catch (error) {
        console.log(error)
      }
    }

    const handleAddAnalysisDocs = (docs) => { analysisDocs.value.push(...docs) }
    const handleDeleteAnalysisDoc = (index) => { analysisDocs.value.splice(index, 1) }
    const handleAddExecutionDocs = (docs) => { executionDocs.value.push(...docs) }
    const handleDeleteExecutionDoc = (index) => { executionDocs.value.splice(index, 1) }
    const handleAddResponseDocs = (docs) => { responseDocs.value.push(...docs) }
    const handleDeleteResponseDoc = (index) => { responseDocs.value.splice(index, 1) }

    const uploadDocs = async (docs, existingKeys = []) => {
      const uploaded = await Promise.all(
        docs.map(file => uploadFileToS3(file, clientId).catch((err) => {
          console.log(err)
          return null
        })),
      )
      return [...existingKeys, ...uploaded.filter(Boolean)]
    }

    const advanceToAnalysis = () => {
      handleAdvance('analysis', { identityVerified: identityVerified.value, identityNote: identityNote.value })
    }

    const advanceToExecution = async () => {
      isUploading.value = true
      try {
        const analysisDocumentKeys = await uploadDocs(analysisDocs.value, request.value.analysisDocumentKeys || [])
        analysisDocs.value = []
        await handleAdvance('execution', { analysisApplicable: analysisApplicable.value, analysisException: analysisException.value, analysisDocumentKeys })
      } finally {
        isUploading.value = false
      }
    }

    const advanceToResponse = async () => {
      isUploading.value = true
      try {
        const executionDocumentKeys = await uploadDocs(executionDocs.value, request.value.executionDocumentKeys || [])
        executionDocs.value = []
        // Notify the newly assigned execution responsible (if it changed)
        const previousResponsible = request.value.executionResponsible?.$oid || request.value.executionResponsible
        if (executionResponsible.value && executionResponsible.value !== previousResponsible) {
          sendResponsibleAssignedNotification({ request: request.value, responsibleId: executionResponsible.value }).catch(err => console.log(err))
        }
        await handleAdvance('response', { executionResponsible: executionResponsible.value, executionActions: executionActions.value, executionDocumentKeys })
      } finally {
        isUploading.value = false
      }
    }

    const handleAdvance = async (newStage, updateData = {}) => {
      if (!hasPermission('privacy-arsopbl-list', 'edit')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      try {
        // Pasar copia profunda para evitar mutaciones
        const originalDataCopy = JSON.parse(JSON.stringify(originalRequest.value))
        await advanceStage(requestId, newStage, updateData, originalDataCopy)
        // Notify data subject on milestone stages
        sendStageChangeNotification({ request: request.value, newStage }).catch(err => console.log(err))
        showSuccessMessage(i18n.t('arsopbl.stage_advanced'))
        await fetchRequest()
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_advance'))
      }
    }

    const handleRejectIdentity = async () => {
      if (!hasPermission('privacy-arsopbl-list', 'edit')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      if (!rejectionReason.value.trim()) {
        showErrorMessage(i18n.t('arsopbl.rejection_reason_required'))
        return
      }
      try {
        // Pasar copia profunda para evitar mutaciones
        const originalDataCopy = JSON.parse(JSON.stringify(originalRequest.value))
        await closeRequest(requestId, 'rejected', {
          identityVerified: false,
          rejectionReason: rejectionReason.value,
        }, originalDataCopy)
        sendClosureNotification({ request: request.value, closureStatus: 'rejected', rejectionReason: rejectionReason.value }).catch(err => console.log(err))
        showSuccessMessage(i18n.t('arsopbl.request_rejected'))
        await fetchRequest()
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_reject'))
      }
    }

    const handleSendResponse = async () => {
      if (!hasPermission('privacy-arsopbl-list', 'edit')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      if (!formalResponse.value.trim()) {
        showErrorMessage(i18n.t('arsopbl.response_required'))
        return
      }
      isUploading.value = true
      try {
        const responseDocumentKeys = await uploadDocs(responseDocs.value, request.value.responseDocumentKeys || [])
        responseDocs.value = []
        // Pasar copia profunda para evitar mutaciones
        const originalDataCopy = JSON.parse(JSON.stringify(originalRequest.value))
        await closeRequest(requestId, 'resolved', {
          formalResponse: formalResponse.value,
          responseDocumentKeys,
        }, originalDataCopy)
        sendClosureNotification({ request: request.value, closureStatus: 'resolved', formalResponse: formalResponse.value }).catch(err => console.log(err))
        showSuccessMessage(i18n.t('arsopbl.response_sent'))
        await fetchRequest()
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_send_response'))
      } finally {
        isUploading.value = false
      }
    }

    const handleDownload = (key) => {
      downloadFile(key)
    }

    onMounted(() => {
      fetchRequest()
      fetchWorkers()
      fetchTemplates()
    })

    return {
      request,
      isLoading,
      isUploading,
      canEdit,
      stageNumber,
      deadlineVariant,
      deadlineDays,
      statusVariant,
      requestStatus,
      rightLabel,
      workersOptions,
      templateOptions,
      selectedTemplate,
      editorOption,
      applyTemplate,
      identityVerified,
      identityNote,
      rejectionReason,
      analysisApplicable,
      analysisException,
      executionResponsible,
      executionActions,
      formalResponse,
      analysisDocs,
      executionDocs,
      responseDocs,
      getStageVariant,
      formatDate,
      handleAdvance,
      advanceToAnalysis,
      advanceToExecution,
      advanceToResponse,
      handleRejectIdentity,
      handleSendResponse,
      handleDownload,
      handleAddAnalysisDocs,
      handleDeleteAnalysisDoc,
      handleAddExecutionDocs,
      handleDeleteExecutionDoc,
      handleAddResponseDocs,
      handleDeleteResponseDoc,
    }
  },
}
</script>
